/**
 * UTM PASSTHROUGH — propaga a query string da URL para os links da página
 *
 * Cola os parâmetros da URL atual (utm_*, fbclid, gclid, ttclid, etc) em todo
 * `href` e todo `data-url` da página. Objetivo: a navegação dentro da LP
 * (LP -> PDP -> checkout) não perde os parâmetros de campanha.
 *
 * Carrega SÓ nas 10 LPs de venda (guard `lp_all_handles` no theme.liquid).
 *
 * ─── RELAÇÃO COM OS OUTROS EIXOS DE ATRIBUIÇÃO ──────────────────────────────
 * Este script NÃO grava nada no cart. Ele só reescreve links no DOM.
 *   _utm_*     (utm-tracker.js)      = QUEM TROUXE   — cart attribute, 30d
 *   _lp_source (lp-source-tracker)   = O QUE CONVERTEU — cart attribute, last-click puro
 *   passthrough (este)               = mantém o ?utm_* VIVO na URL durante a navegação
 *
 * Efeito colateral aceito (decisão do dono, 27/Jul/2026): com a UTM viajando na
 * URL, o utm-tracker.js volta a "ver" a mesma UTM em cada página interna e a
 * reafirma no cart. Na prática a UTM da entrada passa a durar a sessão inteira,
 * mesmo que o _lp_source seja zerado pelo guard. Os dois eixos podem discordar
 * no mesmo pedido (ex: _utm_source=facebook + _lp_source=home). Isso é esperado:
 * um mede mídia, o outro mede funil. Ver documentation.md §4.1.
 *
 * ─── DECISÕES DE IMPLEMENTAÇÃO ──────────────────────────────────────────────
 * 1. MutationObserver: os pop-ups de upsell (ups-*.js) e os motores A/B
 *    (ab-test-*.js) injetam links DEPOIS do DOMContentLoaded. Sem o observer o
 *    fim do funil — justamente o checkout — ficaria descoberto.
 * 2. Guard `data-gd-utm`: com o observer, o mesmo link seria reprocessado a cada
 *    mutação e a query cresceria sem parar. O atributo marca o que já foi feito.
 * 3. Parâmetro existente no link VENCE: se o href já tem `?discount=X`, não
 *    acrescentamos um segundo `discount=`. Os cart permalinks de checkout
 *    (gd-ab-checkout.js) carregam discount, _ab_* e _lp_source próprios. Duplicar
 *    a chave deixaria o Shopify escolher qual vale, de forma não-determinística.
 * 4. Links externos são ignorados: não vaza UTM/fbclid da Guday pra Instagram,
 *    WhatsApp, Okendo etc.
 *
 * Rollback: remover a linha do theme.liquid. Nada persiste (não escreve cookie
 * nem cart) — some no próximo pageview.
 */
(function () {
  'use strict';

  try {
    // Idempotência: não instalar 2x (o asset pode ser incluído por engano em outro guard)
    if (window.__gdUtmPassthrough) return;
    window.__gdUtmPassthrough = true;

    var search = window.location.search;
    if (!search || search === '?') return;

    var incoming;
    try {
      incoming = new URLSearchParams(search);
    } catch (e) {
      return; // navegador sem URLSearchParams: desiste em silêncio
    }

    // Nada útil pra propagar
    var keys = [];
    incoming.forEach(function (_v, k) { if (k) keys.push(k); });
    if (!keys.length) return;

    var SKIP_PREFIX = /^(#|javascript:|tel:|mailto:|sms:|whatsapp:|data:|blob:)/i;
    var HOST = window.location.hostname;

    // Só reescreve link do próprio site. Relativo/âncora-de-path = nosso.
    // Absoluto só se o hostname bater com o atual.
    function isInterno(url) {
      if (/^(https?:)?\/\//i.test(url)) {
        try {
          return new URL(url, window.location.href).hostname === HOST;
        } catch (e) {
          return false;
        }
      }
      return true; // relativo
    }

    function buildUrl(targetUrl) {
      if (!targetUrl) return targetUrl;
      var raw = String(targetUrl).trim();
      if (!raw || SKIP_PREFIX.test(raw)) return targetUrl;
      if (!isInterno(raw)) return targetUrl;

      // Separa o fragmento (#) — a query tem que entrar ANTES dele, senão vira
      // parte do hash e o servidor nunca vê os parâmetros.
      var hash = '';
      var hashAt = raw.indexOf('#');
      if (hashAt !== -1) {
        hash = raw.slice(hashAt);
        raw = raw.slice(0, hashAt);
      }

      var qAt = raw.indexOf('?');
      var base = qAt === -1 ? raw : raw.slice(0, qAt);
      var existing;
      try {
        existing = new URLSearchParams(qAt === -1 ? '' : raw.slice(qAt + 1));
      } catch (e) {
        return targetUrl;
      }

      var mudou = false;
      incoming.forEach(function (value, key) {
        if (!key) return;
        if (existing.has(key)) return; // o link manda: não duplica a chave
        existing.append(key, value);
        mudou = true;
      });
      if (!mudou) return targetUrl;

      var qs = existing.toString();
      return base + (qs ? '?' + qs : '') + hash;
    }

    function apply(el) {
      if (!el || el.nodeType !== 1) return;
      if (el.getAttribute('data-gd-utm') === '1') return;

      var href = el.getAttribute('href');
      var dataUrl = el.getAttribute('data-url');
      if (href === null && dataUrl === null) return;

      if (href !== null) {
        var nh = buildUrl(href);
        if (nh !== href) el.setAttribute('href', nh);
      }
      if (dataUrl !== null) {
        var nd = buildUrl(dataUrl);
        if (nd !== dataUrl) el.setAttribute('data-url', nd);
      }
      el.setAttribute('data-gd-utm', '1');
    }

    var SEL = 'a[href], [data-url]';

    function scan(root) {
      try {
        if (!root || root.nodeType !== 1) return;
        if (root.matches && root.matches(SEL)) apply(root);
        var found = root.querySelectorAll ? root.querySelectorAll(SEL) : [];
        for (var i = 0; i < found.length; i++) apply(found[i]);
      } catch (e) {}
    }

    function applyToPage() {
      scan(document.body || document.documentElement);
    }

    // Links criados depois do load (pop-ups de upsell, motores A/B, cart drawer).
    // Fica ligado a página toda: os pop-ups abrem no clique, muito depois do load.
    function observe() {
      try {
        if (!window.MutationObserver) return;
        var alvo = document.body;
        if (!alvo) return;
        new MutationObserver(function (muts) {
          for (var i = 0; i < muts.length; i++) {
            var m = muts[i];
            if (m.type === 'childList') {
              for (var j = 0; j < m.addedNodes.length; j++) scan(m.addedNodes[j]);
            } else if (m.type === 'attributes' && m.target) {
              // href reescrito por outro script: limpa a marca e reprocessa
              if (m.target.getAttribute) m.target.removeAttribute('data-gd-utm');
              apply(m.target);
            }
          }
        }).observe(alvo, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['href', 'data-url']
        });
      } catch (e) {}
    }

    function init() {
      applyToPage();
      observe();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  } catch (e) {
    /* silencioso por design: atribuição nunca pode quebrar a página */
  }
})();
